import { Stack } from '@mantine/core';
import { FILTER_OPTIONS_DIVIDER } from '@repo/common/components/Filter';
import { FilterItemError } from '@repo/common/components/Filter/FilterItemError';
import { FilterItemSkeleton } from '@repo/common/components/Filter/FilterItemSkeleton';
import { ListBox } from '@repo/foundations';
import { range } from 'lodash-es';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import type { EditBlockStore } from '../../store';
import type { EditItem } from '../../types';
import { EditValueType } from '../../types';
import { EditActionValueItem } from './EditActionValueItem';

export interface EditActionValueDropdownListProps {
	store: EditBlockStore;
	actionSearchKey: string;
	onItemClick: (
		value: string | boolean,
		shouldClose: boolean,
		label?: string
	) => void;
}

export const EditActionValueDropdownList = observer(
	({
		store,
		actionSearchKey,
		onItemClick,
	}: EditActionValueDropdownListProps) => {
		const editAction = store.editActions.get(actionSearchKey)!;

		const onSelect = useCallback(
			(item: EditItem) => (close: boolean) => {
				if (!Array.isArray(item.value)) {
					onItemClick(item.value, close, item.label);
				}
			},
			[onItemClick]
		);

		const onDeselect = useCallback(
			(item: EditItem) => (close: boolean) => {
				if (!Array.isArray(item.value)) {
					onItemClick(item.value, close, item.label);
				}
			},
			[onItemClick]
		);

		const options = useMemo(() => {
			const result: Array<EditItem | typeof FILTER_OPTIONS_DIVIDER> = [
				...toJS(editAction.selectedItems),
			];

			if (
				editAction.selectedItems.length > 0 &&
				editAction.unselectedItems.length
			) {
				result.push(FILTER_OPTIONS_DIVIDER);
			}

			result.push(...toJS(editAction.unselectedItems));

			return result;
		}, [editAction.selectedItems, editAction.unselectedItems]);

		return (
			<ListBox.ItemsDropdown
				search={{
					onChange: editAction.setSearchTerm,
					value: editAction.searchTerm,
				}}
				items={options}
				renderItem={(item, getProps, idx) => {
					if (item === FILTER_OPTIONS_DIVIDER) {
						return <ListBox.Divider key={`divider-${idx}`} />;
					}

					return (
						<EditActionValueItem
							key={`edit-action-value-${item.value}`}
							item={item}
							editValueType={EditValueType.MULTIPLE}
							selected={editAction.editAction.value === item.value}
							onClick={
								editAction.editAction.value === item.value
									? onDeselect(item)
									: onSelect(item)
							}
							getProps={getProps}
						/>
					);
				}}
			>
				{editAction.loading && (
					<Stack spacing="2xs">
						{range(3).map((index) => (
							<FilterItemSkeleton key={index} />
						))}
					</Stack>
				)}
				{editAction.error && (
					<FilterItemError>An error has ocurred</FilterItemError>
				)}
			</ListBox.ItemsDropdown>
		);
	}
);
